import {MeterButton} from '@github-ui/copilot-chat/components/MeterButton'
import {ChatComposer, type ComposerAttachment} from '@github-ui/agent-sessions-interface/components/ChatComposer'
import {RepoPicker} from '@github-ui/agent-sessions-interface/components/RepoPicker'
import {createScopedRepositoryLoader} from '@github-ui/agent-sessions-interface/components/repo-picker/scope'
import {useCallback, useEffect, useImperativeHandle, useLayoutEffect, useMemo, useRef} from 'react'
import {clsx} from 'clsx'
import type {ImageReference} from '@github-ui/copilot-chat-types'
import {FilePickerPanel} from '@github-ui/copilot-shared-ui/components/FilePickerPanel'
import {fetchRepos} from '@github-ui/copilot-shared-ui/repos/fetch-repos'

import {
  EnvironmentAttachmentControls,
  EnvironmentImageAttachments,
} from '@github-ui/one-copilot-composer/EnvironmentAttachments'
import {ImageReferenceToken} from '@github-ui/one-copilot-composer/ImageReferenceToken'
import {SpacePicker} from '@github-ui/one-copilot-composer/SpacePicker'
import {ComposerToolbar} from '@github-ui/one-copilot-composer/ComposerToolbar'
import {EnvironmentStrip} from '@github-ui/one-copilot-composer/environment-strip/EnvironmentStrip'
import {availableAgentModes, type TaskComposerProps} from '@github-ui/one-copilot-composer/shared'
import {useComposerFileAttach} from '@github-ui/copilot-shared-ui/hooks/use-composer-file-attach'
import {
  useComposerReferenceActivation,
  type ComposerActivationTarget,
} from '@github-ui/copilot-shared-ui/hooks/use-composer-reference-activation'
import {useMarkdownImageHandling} from '@github-ui/one-copilot-composer/use-markdown-image-handling'
import {useTaskComposer, useTaskComposerTargetState} from '@github-ui/one-copilot-composer/use-task-composer'
import {isSessionTokenMeterApplicable} from '@github-ui/one-copilot-composer/use-environment-selection'
import styles from './TaskComposer.module.css'
import {MAX_AGENT_TASK_PROMPT_LENGTH} from '@github-ui/copilot-shared-ui/composer/max-prompt-length'
import {QuotaBanner} from '../task-view/QuotaBanner'
import {
  SELECTED_SPACE_ATTACHMENT_ID,
  selectedSpaceAttachment,
} from '@github-ui/one-copilot-composer/spaces/space-attachment'

import {ChronicleAutocomplete} from '../chronicle-autocomplete/ChronicleAutocomplete'
import {ScopePicker} from './ScopePicker'
import type {EarlyTaskProvisioning} from '../../ahp/early-task-provisioning'
import type {NewSessionProvisioningContext} from '@github-ui/one-copilot-composer/new'

const REFERENCE_ATTACHMENT_ID = 'reference'

function ImageAttachmentPreview({references}: {references: ImageReference[]}) {
  return (
    <ul className={styles.imageAttachments} aria-label="Attached images">
      {references.map(reference => (
        <li key={reference.id}>
          <ImageReferenceToken reference={reference} size="small" />
        </li>
      ))}
    </ul>
  )
}

export function TaskComposer(props: TaskComposerProps & {earlyProvisioning?: EarlyTaskProvisioning}) {
  const {
    hideRepoSelector,
    chatInputRef,
    textAreaRef: textAreaRefFromProps,
    className,
    shouldClearTextOnSubmit = true,
    submitWithModifier = false,
    disableWhileSubmitting = false,
    disableSandboxSubmission = false,
    isNewConversation,
    agentQuotaSnapshot,
    initialBranch,
    maxTextareaHeight,
    earlyProvisioning,
  } = props

  const targetState = useTaskComposerTargetState()
  const images = useMarkdownImageHandling({
    textAreaRef: targetState.textAreaRef,
    repositoryId: targetState.selectedRepo?.id?.toString(),
  })

  const controller = useTaskComposer(props, {targetState, images})

  const {
    textAreaRef,
    setCurrentPrompt,
    currentPrompt,
    handleTextChange,
    handleChronicleSelect,
    setChronicleDismissed,
    chronicleSuggestions,
    onSubmit,
    onPaste,
    dropTargetProps,
    isDraggedOver,
    isImageUploading,
    isBlockedByQuota,
    isAhpEnvironment,
    isSandboxEnvironment,
    isSandboxDraft,
    isSandboxPolicyBlockingSubmission,
    isEnvironmentSubmitting,
    isSelectedEnvironmentSubmissionAvailable,
    isRepositorySelectionValid,
    targetSelection,
    selectedEnvironment,
    showEnvironmentPicker,
    showSandboxOption,
    selectedBranch,
    setSelectedBranch,
    initialNameWithOwner,
    hasSpecifiedRepository,
    branchParam,
    setSelectedEnvironment,
    scopeOwner,
    pickerScopeOwner,
    handleTargetSelection,
    resolvedUserLogin,
    selectedScope,
    setSelectedScope,
    availableOrganizations,
    currentUserAvatarUrl,
    reference,
    setReference,
    chatReference,
    imageReferences,
    isModelSelectionReady,
    isTokenBasedBillingEnabled,
    environmentAttachments,
    canPrepareChat,
    selectedSpace,
    setSelectedSpace,
    isSpaceResolving,
    setIsSpaceResolving,
    spaceSelectionError,
    setSpaceSelectionError,
  } = controller

  useImperativeHandle<HTMLTextAreaElement | null, HTMLTextAreaElement | null>(
    textAreaRefFromProps ?? null,
    () => textAreaRef.current,
  )
  useImperativeHandle(chatInputRef, () => ({setText: setCurrentPrompt}), [setCurrentPrompt])

  const fileInputRef = useRef<HTMLInputElement>(null)
  const attachButtonRef = useRef<HTMLButtonElement>(null)
  const submitButtonRef = useRef<HTMLButtonElement>(null)
  const openFilePicker = useCallback(() => fileInputRef.current?.click(), [])

  const attachments = useMemo<ComposerAttachment[] | undefined>(() => {
    const combined: ComposerAttachment[] = []
    // The Space picker lives inside the attach ("+") menu, which closes on
    // selection and unmounts its label; surface the selection as a persistent
    // token so the chosen Space stays visible while composing.
    if (targetSelection.kind === 'chat') {
      const spaceToken = selectedSpaceAttachment(selectedSpace)
      if (spaceToken) combined.push(spaceToken)
    }
    if (chatReference && reference) {
      const label = reference.range
        ? `${reference.path}#L${reference.range.start}-L${reference.range.end}`
        : reference.path
      combined.push({
        id: REFERENCE_ATTACHMENT_ID,
        label,
        icon: reference.range ? 'snippet' : 'file',
        ...(chatReference.url ? {url: chatReference.url} : {}),
      })
    }
    if (isAhpEnvironment) combined.push(...environmentAttachments.attachments)
    return combined.length > 0 ? combined : undefined
  }, [
    targetSelection.kind,
    selectedSpace,
    chatReference,
    reference,
    isAhpEnvironment,
    environmentAttachments.attachments,
  ])

  const handleRemoveAttachment = useCallback(
    (id: string) => {
      if (id === SELECTED_SPACE_ATTACHMENT_ID) {
        setSelectedSpace(undefined)
        return
      }
      if (id === REFERENCE_ATTACHMENT_ID) {
        setReference(null)
        return
      }
      environmentAttachments.handleRemoveAttachment(id)
    },
    [setSelectedSpace, setReference, environmentAttachments],
  )

  const isChatTarget = targetSelection.kind === 'chat'
  const activationTargets = useMemo<ComposerActivationTarget[]>(
    () => [
      ...(chatReference
        ? [{id: REFERENCE_ATTACHMENT_ID, target: {kind: 'reference' as const, reference: chatReference}}]
        : []),
      ...(selectedSpace
        ? [
            {
              id: SELECTED_SPACE_ATTACHMENT_ID,
              target: {kind: 'space' as const, owner: selectedSpace.owner, number: selectedSpace.id},
            },
          ]
        : []),
    ],
    [chatReference, selectedSpace],
  )
  const referenceActivation = useComposerReferenceActivation(environmentAttachments, attachments, activationTargets)

  const composerDisabled =
    isBlockedByQuota ||
    isEnvironmentSubmitting ||
    isSandboxPolicyBlockingSubmission ||
    !isRepositorySelectionValid ||
    (isChatTarget && !canPrepareChat) ||
    isImageUploading ||
    (isAhpEnvironment
      ? disableSandboxSubmission || !selectedEnvironment?.environmentId || !isSelectedEnvironmentSubmissionAvailable
      : !isModelSelectionReady || isImageUploading) ||
    isSpaceResolving
  const provisioningContext = useMemo<NewSessionProvisioningContext>(
    () => ({
      target: targetSelection.kind === 'chat' ? 'chat' : 'task',
      repository: targetSelection.kind === 'repository' ? targetSelection.repository.nameWithOwner : undefined,
      branch: selectedBranch,
      environmentId: selectedEnvironment?.environmentId,
      space: selectedSpace ? {owner: selectedSpace.owner, slug: selectedSpace.slug} : undefined,
    }),
    [targetSelection, selectedBranch, selectedEnvironment?.environmentId, selectedSpace],
  )
  useLayoutEffect(() => {
    earlyProvisioning?.onContextChange(provisioningContext)
    if (isBlockedByQuota || isSandboxPolicyBlockingSubmission || !isRepositorySelectionValid) {
      earlyProvisioning?.onAbandonSubmission()
    }
  }, [
    earlyProvisioning,
    provisioningContext,
    isBlockedByQuota,
    isSandboxPolicyBlockingSubmission,
    isRepositorySelectionValid,
  ])
  useEffect(() => () => earlyProvisioning?.onDispose(), [earlyProvisioning])
  const onInteraction = useCallback(() => {
    if (!composerDisabled) earlyProvisioning?.onRepositoryInteraction(provisioningContext)
  }, [composerDisabled, earlyProvisioning, provisioningContext])
  const onComposerInteraction = useCallback(
    (event: React.SyntheticEvent<HTMLDivElement>) => {
      if (event.target instanceof Node && submitButtonRef.current?.contains(event.target)) return
      if (event.type === 'keydown' && event.target === textAreaRef.current) return
      onInteraction()
    },
    [onInteraction, textAreaRef],
  )
  const submit = useCallback<typeof onSubmit>(
    async (...args) => {
      earlyProvisioning?.onBeforeSubmit()
      try {
        const result = await onSubmit(...args)
        earlyProvisioning?.onAbandonSubmission()
        return result
      } catch (error) {
        earlyProvisioning?.onAbandonSubmission()
        throw error
      }
    },
    [earlyProvisioning, onSubmit],
  )
  useComposerFileAttach(environmentAttachments, isAhpEnvironment && !composerDisabled)
  const scopedRepoLoader = useMemo(
    () =>
      createScopedRepositoryLoader((query, signal) => fetchRepos(query, signal, pickerScopeOwner), pickerScopeOwner),
    [pickerScopeOwner],
  )
  // A Cloud session spends the same premium quota whichever destination the
  // draft targets, so the meter follows the environment, not the target.
  const showSessionMeter = isSessionTokenMeterApplicable({isTokenBasedBillingEnabled, isSandboxEnvironment})
  const modeControl = props.modePicker?.({
    availableModes: availableAgentModes(isAhpEnvironment),
    supportsApprovalMode: isAhpEnvironment,
  })

  return (
    <div className={styles.inputWrapper}>
      <QuotaBanner agentQuotaSnapshot={agentQuotaSnapshot} />
      <div className={styles.autocompleteWrapper}>
        <ChronicleAutocomplete
          suggestions={chronicleSuggestions}
          onSelect={handleChronicleSelect}
          onDismiss={() => setChronicleDismissed(true)}
          textAreaRef={textAreaRef}
          currentText={currentPrompt}
        />
        <div
          className={clsx(className, isDraggedOver && styles.isDraggedOver)}
          {...dropTargetProps}
          onClickCapture={onComposerInteraction}
          onKeyDownCapture={onComposerInteraction}
        >
          <ChatComposer
            value={currentPrompt}
            onValueChange={handleTextChange}
            textAreaRef={textAreaRef}
            submitButtonRef={submitButtonRef}
            onSubmit={submit}
            onInputMutation={onInteraction}
            placeholder={isChatTarget ? 'Send a message' : 'Give Copilot a background task to work on'}
            ariaLabel={isChatTarget ? 'Send a message' : 'Coding task prompt'}
            containerClassName={clsx(
              styles.composerMinHeight,
              isNewConversation && styles.composerNewConversationMinHeight,
            )}
            inputTextSize="responsive"
            submitWithModifier={submitWithModifier}
            clearOnSubmit={shouldClearTextOnSubmit}
            disableWhileSubmitting={disableWhileSubmitting || isAhpEnvironment}
            maxLength={MAX_AGENT_TASK_PROMPT_LENGTH}
            maxTextareaHeight={maxTextareaHeight}
            onPaste={isAhpEnvironment ? environmentAttachments.handlePaste : onPaste}
            attachments={referenceActivation.attachments}
            hasAttachedContent={isAhpEnvironment && environmentAttachments.hasAttachments}
            {...(isAhpEnvironment
              ? {
                  onFilesAdded: environmentAttachments.handleFilesAdded,
                  onAttachFiles: environmentAttachments.openFilePicker,
                  onUploadFiles: openFilePicker,
                  onAttachRepo: environmentAttachments.openRepoPicker,
                  attachButtonRef,
                  ...(environmentAttachments.images.length > 0
                    ? {attachmentPreview: <EnvironmentImageAttachments controller={environmentAttachments} />}
                    : {}),
                }
              : imageReferences.length > 0
                ? {attachmentPreview: <ImageAttachmentPreview references={imageReferences} />}
                : {})}
            onRemoveAttachment={handleRemoveAttachment}
            onAttachmentClick={referenceActivation.onAttachmentClick}
            disabled={composerDisabled}
            attachMenuContent={
              isAhpEnvironment ? (
                <SpacePicker
                  selectedSpace={selectedSpace}
                  onSelectSpace={setSelectedSpace}
                  isResolving={isSpaceResolving}
                  onResolvingChange={setIsSpaceResolving}
                  selectionError={spaceSelectionError}
                  onSelectionErrorChange={setSpaceSelectionError}
                  disabled={!isSandboxDraft}
                  disabledDescription="Spaces can only be attached when starting a new cloud session."
                />
              ) : undefined
            }
            toolbarContent={<ComposerToolbar controller={controller} modeControl={modeControl} />}
            trailingToolbarContent={
              showSessionMeter ? (
                <>
                  <MeterButton />
                </>
              ) : undefined
            }
          />
        </div>
      </div>
      {isAhpEnvironment && (
        <>
          <EnvironmentAttachmentControls controller={environmentAttachments} fileInputRef={fileInputRef} />
          {environmentAttachments.filePickerOpen && (
            <FilePickerPanel
              key={`files:${pickerScopeOwner ?? 'unresolved'}`}
              isOpen
              onClose={environmentAttachments.closeFilePicker}
              onSelect={environmentAttachments.handleFilePickerSelect}
              repositoryLoader={scopedRepoLoader}
              scopeOwner={pickerScopeOwner}
            />
          )}
          <RepoPicker
            key={`repos:${pickerScopeOwner ?? 'unresolved'}`}
            anchorRef={attachButtonRef}
            repositoryLoader={scopedRepoLoader}
            selectedRepos={environmentAttachments.selectedRepos}
            onUpdateRepos={environmentAttachments.handleUpdateRepos}
            triggerOpen={environmentAttachments.repoPickerOpen}
            onClose={environmentAttachments.closeRepoPicker}
            anchorSide="outside-top"
          />
        </>
      )}
      <EnvironmentStrip
        hideRepoSelector={hideRepoSelector}
        initialRepoNWO={initialNameWithOwner}
        initialBranch={initialBranch ?? branchParam ?? undefined}
        targetSelection={targetSelection}
        onTargetSelectionChange={handleTargetSelection}
        selectedBranch={selectedBranch}
        onBranchSelect={(branch: string | undefined) => setSelectedBranch(branch || '')}
        currentPrompt={currentPrompt}
        currentUserLogin={resolvedUserLogin}
        selectedEnvironment={selectedEnvironment}
        onSelectEnvironment={setSelectedEnvironment}
        includeSandboxOption={showSandboxOption}
        allowChat={canPrepareChat && !hasSpecifiedRepository}
        showEnvironmentPicker={showEnvironmentPicker}
        disabled={isEnvironmentSubmitting}
        scopePicker={
          // The scope belongs to the Sandbox environment: it decides which
          // account owns the sandbox and which repositories the picker
          // offers. Selecting Sandbox switches the destination to Chat on its
          // own, so gating this on the Chat launcher hid the picker for the
          // repoless Chat draft that Sandbox actually opens on.
          isSandboxEnvironment && selectedScope && resolvedUserLogin && availableOrganizations.length > 0 ? (
            <ScopePicker
              selectedScope={selectedScope}
              onSelectScope={setSelectedScope}
              organizations={availableOrganizations}
              currentUserLogin={resolvedUserLogin}
              currentUserAvatarUrl={currentUserAvatarUrl}
              size="small"
              disabled={isEnvironmentSubmitting}
            />
          ) : undefined
        }
        scopeOwner={scopeOwner}
      />
    </div>
  )
}
